// pages/BankCards.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, Form, Button,
  InputGroup, Dropdown, Spinner, Alert
} from 'react-bootstrap';
import ViewCardModal from '../components/modals/BankCardModals/ViewCardModal';
import CardFormModal from '../components/modals/BankCardModals/CardFormModal';
import DeleteCardModal from '../components/modals/BankCardModals/DeleteCardModal';
import AddToInheritanceModal from '../components/modals/AddToInheritanceModal';
import Sidebar from '../components/dashboard/Sidebar';
import ListHeader from '../components/common/ListHeader';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import {
  fetchBankCards,
  viewBankCard,
  saveBankCard,
  deleteBankCard,
  generateUniqueBankCardAlias,
  addCardToInheritance
} from '../services/bankcard.service';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { checkPaymentStatus, checkStorageLimits, formatFileSize, formatItemDate, removeTxtExtension } from '../services/api.service';
import InheritanceDropdownItem from '../components/common/InheritanceDropdownItem';
import PropertiesModal from '../components/common/PropertiesModal';
import { logEvent } from '../analytics';

import '../styles/pages/bankcards.css';
import '../styles/components/modals.css';

const Banking = () => {
  // State for bank cards data
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCards, setSelectedCards] = useState([]);
  const [sidebarToggled, setSidebarToggled] = useState(false);

  // State for search and sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState('none');

  // State for modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [multipleDeleteModalOpen, setMultipleDeleteModalOpen] = useState(false);
  const [inheritanceModalOpen, setInheritanceModalOpen] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingToInheritance, setIsAddingToInheritance] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  // Add state to track which card's dropdown is open
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);

  // Refs for dropdown handling
  const dropdownRefs = useRef({});
  const cardRefs = useRef({});
  const ignoreNextClick = useRef(false);

  // Get auth context
  const { token, user } = useAuth();
  const { isAccountFrozen, showFrozenAlert } = usePaymentStatus();

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  // Load bank cards
  useEffect(() => {
    loadBankCards();
  }, [token]);

  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Add effect to handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }

      const isInsideAnyDropdown = Object.values(dropdownRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      const isInsideAnyCard = Object.values(cardRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      if (!isInsideAnyDropdown && !isInsideAnyCard) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadBankCards = async (forceClearCache = false) => {
    setIsLoading(true);
    try {
      // Clear any relevant cached data if force refresh
      if (forceClearCache) {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('cache-bucket-bankcards')) {
            localStorage.removeItem(key);
          }
        });
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('cache-bucket-bankcards')) {
            sessionStorage.removeItem(key);
          }
        });
      }

      // Pass the forceClearCache flag to fetchBankCards
      const data = await fetchBankCards(token, forceClearCache);
      setCards(data);
    } catch (error) {
      console.error('Error loading bank cards:', error);
      showAlert(`Error loading bank cards: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Alert helper
  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Handle view card
  const handleViewCard = async (card, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setIsLoading(true);
      const cardData = await viewBankCard(card.name, token);
      setCurrentCard(cardData);
      setViewModalOpen(true);
    } catch (error) {
      showAlert(`Error viewing card: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit card
  const handleEditCard = async (card, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setIsLoading(true);
      const cardData = await viewBankCard(card.name, token);
      setCurrentCard(cardData);
      setIsEditing(true);
      setFormModalOpen(true);

      // Check payment status in the background
      checkCardPermissions();
    } catch (error) {
      showAlert(`Error loading card for editing: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const checkCardPermissions = async () => {
    try {
      // Check payment status
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        setFormModalOpen(false);
        return;
      }

      // Check storage limits
      const storageCheck = await checkStorageLimits(153600, token);
      if (!storageCheck.withinLimits) {
        showAlert(`Storage limit exceeded. You have ${formatFileSize(storageCheck.remainingSpace)} remaining.`, 'warning');
        setFormModalOpen(false);
        return;
      }
    } catch (error) {
      showAlert(`Error: ${error.message}`, 'danger');
      setFormModalOpen(false);
    }
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      // Select all filtered cards
      filteredCards.forEach((card) => {
        if (!selectedCards.includes(card.name)) {
          setSelectedCards(prev => [...prev, card.name]);
        }
      });
    } else {
      // Deselect all cards
      setSelectedCards([]);
    }
  };

  const areAllCardsSelected = () => {
    if (filteredCards.length === 0) return false;
    return filteredCards.every(card => selectedCards.includes(card.name));
  };

  const areSomeCardsSelected = () => {
    return selectedCards.length > 0 && !areAllCardsSelected();
  };


  // Handle add card
  const handleAddCard = () => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      return;
    }
    // Open modal immediately
    setCurrentCard(null);
    setIsEditing(false);
    setFormModalOpen(true);

    // Check permissions in the background
    checkCardPermissions();
  };



  // Handle delete card
  const handleDeleteCard = (card, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setCurrentCard(card);
    setDeleteModalOpen(true);
  };

  // Handle add to inheritance
  const handleAddToInheritance = async (card, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsAddingToInheritance(true);
    try {
      // Check payment status first
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
             logEvent('inheritance_add_item', {
       item_type: 'bank_card',
       item_alias: card?.name || 'unknown',
       add_result: 'blocked_overdue'
     });
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        return;
      }

      // Import the function from bankcard service
      const { addCardToCurrentInheritance } = await import('../services/bankcard.service');

      // Add the card directly to the current inheritance
      await addCardToCurrentInheritance(card.name, token);

         logEvent('inheritance_add_item', {
     item_type: 'bank_card',
     item_alias: card?.name || 'unknown',
     add_result: 'success'
   });

      showAlert('Bank card successfully added to endowment', 'success');
    } catch (error) {
         logEvent('inheritance_add_item', {
     item_type: 'bank_card',
     item_alias: card?.name || 'unknown',
     add_result: 'failure',
     error_message: error?.message || 'unknown'
   });
      showAlert(`Error adding card to endowment: ${error.message}`, 'danger');
    } finally {
      setIsAddingToInheritance(false);
    }
  };

  // Handle delete selected cards
  const handleDeleteSelected = () => {
    if (selectedCards.length > 0) {
      setMultipleDeleteModalOpen(true);
    }
  };

  // Confirm delete card
  const confirmDeleteCard = async () => {
    setIsDeleting(true);
    try {
      await deleteBankCard(currentCard.name, token);
      showAlert('Bank card deleted successfully', 'success');
      setDeleteModalOpen(false);
      setCurrentCard(null);
      loadBankCards();
    } catch (error) {
      showAlert(`Error deleting bank card: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Confirm delete multiple cards
  const confirmDeleteMultiple = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = selectedCards.map(name =>
        deleteBankCard(name, token)
      );

      await Promise.all(deletePromises);
      showAlert('Selected bank cards deleted successfully', 'success');
      setMultipleDeleteModalOpen(false);
      setSelectedCards([]);
      loadBankCards();
    } catch (error) {
      showAlert(`Error deleting bank cards: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Confirm add to inheritance
  const confirmAddToInheritance = async (inheritanceName) => {
    setIsAddingToInheritance(true);
    try {
      await addCardToInheritance(currentCard.originalName, inheritanceName, token);
      showAlert('Bank card successfully added to endowment', 'success');
      setInheritanceModalOpen(false);
    } catch (error) {
      showAlert(`Error adding card to endowment: ${error.message}`, 'danger');
    } finally {
      setIsAddingToInheritance(false);
    }
  };

  // Save card
  const handleSaveCard = async (formData, originalName) => {
  setIsSaving(true);
 const action = originalName ? 'update' : 'create';
 const requestedAlias = formData.alias;
 let aliasChanged = false;
  try {
    if (!originalName) {
      // For new cards, ensure alias is unique
      formData.alias = await generateUniqueBankCardAlias(formData.alias, token);
      aliasChanged = formData.alias !== requestedAlias;
    } else {
      // For editing, check if the alias has changed
      const originalAlias = removeTxtExtension(originalName);
      if (formData.alias !== originalAlias) {
        // Alias changed during editing, ensure new alias is unique
        formData.alias = await generateUniqueBankCardAlias(formData.alias, token);
        aliasChanged = true;
      }
    }

    await saveBankCard(formData, originalName, token);
    showAlert('Bank card saved successfully', 'success');
    setFormModalOpen(false);
    loadBankCards();
       logEvent('vault_item_save', {
     item_type: 'bank_card',
     action,
     alias: formData.alias,
     alias_changed: aliasChanged,
     save_result: 'success'
   });
    return true;
  } catch (error) {
    showAlert(`Error saving bank card: ${error.message}`, 'danger');
       logEvent('vault_item_save', {
     item_type: 'bank_card',
     action,
     alias: formData.alias,
     alias_changed: aliasChanged,
     save_result: 'failure',
     error_message: error?.message || 'unknown'
   });
    throw error;
  } finally {
    setIsSaving(false);
  }
};

  // Handle checkbox selection
  const handleSelectCard = (name, isSelected, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (isSelected) {
      setSelectedCards(prev => [...prev, name]);
    } else {
      setSelectedCards(prev => prev.filter(item => item !== name));
    }
  };

  // Toggle dropdown menu visibility
  const handleCardClick = (cardId, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Set a timeout for single click action
    const timeout = setTimeout(() => {
      // Toggle dropdown when clicking the card
      setOpenDropdownId(prevId => prevId === cardId ? null : cardId);
      setClickTimeout(null);
    }, 250); // 250ms delay to detect double-click

    setClickTimeout(timeout);

  };

  const handleCardDoubleClick = (card, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear the single-click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Close dropdown if it's open
    setOpenDropdownId(null);

    // Execute the view action (same as clicking "View" in dropdown)
    handleViewCard(card, e)
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle sort
  const handleSort = (type) => {
    setSortType(type);
    let sortedCards = [...cards];

    switch (type) {
      case 'nameAsc':
        sortedCards.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'nameDesc':
        sortedCards.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'dateAsc':
        sortedCards.sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));
        break;
      case 'dateDesc':
        sortedCards.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
        break;
      default:
        break;
    }

    setCards(sortedCards);
  };

  // Filter cards based on search query
  const filteredCards = cards.filter(card =>
    removeTxtExtension(card.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get sort button text
  const getSortButtonText = () => {
    switch (sortType) {
      case 'nameAsc': return 'Name (A-Z)';
      case 'nameDesc': return 'Name (Z-A)';
      case 'dateAsc': return 'Date (Oldest)';
      case 'dateDesc': return 'Date (Newest)';
      default: return 'Sort: None';
    }
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div id="content-wrapper" className={`d-flex flex-column ${sidebarToggled ? 'content-toggled' : ''}`}>
          <div id="content">
            <DashboardHeader toggleSidebar={() => setSidebarToggled(!sidebarToggled)} />

            <div className="container-fluid">
              {/* Alert */}
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert(prev => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}

              <div className="row">
                <div className="col-lg-12">
                  <div className="card header-sort shadow mb-4" style={{ height: 'auto', display: 'flex', flexDirection: 'column', padding: '5px', overflowX: 'hidden' }}>
                    {/* Use the reusable list header component */}
                    <ListHeader
                      title="Bank Cards"
                      searchQuery={searchQuery}
                      onSearch={handleSearch}
                      sortType={sortType}
                      onSort={handleSort}
                      onRefresh={() => loadBankCards(true)} // Pass true to force cache clear
                      isLoading={isLoading}
                      searchPlaceholder="Search bank cards..."
                    />

                    {filteredCards.length > 0 && (
                      <div className="select-all-container p-2 border-bottom">
                        <div className="d-flex align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="selectAllCardsCheckbox"
                              checked={areAllCardsSelected()}
                              ref={(input) => {
                                if (input) input.indeterminate = areSomeCardsSelected();
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <label className="form-check-label ms-2" htmlFor="selectAllCardsCheckbox">
                              {selectedCards.length > 0
                                ? `${selectedCards.length} of ${filteredCards.length} selected`
                                : `Select all ${filteredCards.length} cards`
                              }
                            </label>
                          </div>

                          
                        </div>
                      </div>
                    )}

                    <div className="card-body header-sort p-0" style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '5px' }}>
                      <div id="cardBody" style={{ minHeight: '100%', backgroundColor: '#ffffff', color: '#000000', paddingBottom: '25px' }}>
                        <div style={{ paddingBottom: '25px' }}></div>
                        {isLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border" role="status" style={{ color: '#00CC5C' }}>
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        ) : filteredCards.length === 0 ? (
                          <div className="empty-state text-center py-5">
                            <i className="fas fa-credit-card fa-3x text-black mb-3"></i>
                            <h5 className="text-black">No Bank Cards Added Yet</h5>
                            <p className="text-black">Your saved bank cards will appear here.</p>
                            <button
                              className="btn btn-sm mt-2"
                              style={{
                                backgroundColor: '#00CC5C',
                                color: '#FFFFFF',
                                padding: '4px 10px',
                                fontSize: '14px',
                                width: 'auto',
                                display: 'inline-block',
                              }}
                              onClick={handleAddCard}
                              id="addNewCardBtn"
                            >
                              <i className="fas fa-plus-circle" style={{ fontSize: '20px' }}></i>Add New Card
                            </button>
                          </div>
                        ) : (

                          filteredCards.map(card => (
                            <div
                              key={card.id}
                              className="item d-flex align-items-center card-item-container"
                              id={`card-${card.id}`}
                              ref={el => cardRefs.current[card.id] = el}
                              onClick={(e) => handleCardClick(card.id, e)}
                              onDoubleClick={(e) => handleCardDoubleClick(card, e)}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              <div className="item-content d-flex align-items-center flex-grow-1">
                                <div className="item-check" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${card.id}`}
                                    className="form-check-input item-checkbox"
                                    checked={selectedCards.includes(card.name)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => handleSelectCard(card.name, e.target.checked, e)}
                                    style={{ width: '18px', height: '18px', margin: '0' }}
                                  />
                                </div>
                                <div className="item-icon" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <i className="fas fa-credit-card" style={{ color: '#00CC5C', fontSize: '24px' }}></i>
                                </div>
                                <div className="item-name text-truncate" style={{ paddingRight: '10px' }} title={removeTxtExtension(card.name)}>
                                  {removeTxtExtension(card.name)}
                                </div>
                                <div className="item-date text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatItemDate(card.lastModified)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatFileSize(card.size)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '20px' }}>
                                  <i className="fas fa-ellipsis-v" style={{ color: '#00CC5C' }}></i>
                                </div>

                              </div>

                              {/* Dropdown menu positioned absolutely */}
                              <div
                                ref={el => dropdownRefs.current[card.id] = el}
                                className={`dropdown-menu dropdown-menu-right ${openDropdownId === card.id ? 'show' : ''}`}
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: '0',
                                  zIndex: 1000,
                                  display: openDropdownId === card.id ? 'block' : 'none',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`view-${card.id}`}
                                  onClick={(e) => handleViewCard(card, e)}
                                >
                                  <i className="fas fa-eye fa-sm fa-fw mr-2 text-black-400"></i>View
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`edit-${card.id}`}
                                  onClick={(e) => handleEditCard(card, e)}
                                >
                                  <i className="fas fa-pencil-alt fa-sm fa-fw mr-2 text-black-400"></i>Edit
                                </a>
                                {user?.SubscriptionType !== "1" && user?.SubscriptionType !== "3" && (
                                  <InheritanceDropdownItem
                                    onClick={(e) => handleAddToInheritance(card, e)}
                                    isLoading={isAddingToInheritance}
                                    defaultText="Add to Endowment"
                                    loadingText="Adding..."
                                  />
                                )}
                                <div className="dropdown-divider"></div>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`delete-${card.id}`}
                                  data-action="delete-item"
                                  data-item-name={card.name}
                                  data-item-type="card"
                                  onClick={(e) => handleDeleteCard(card, e)}
                                >
                                  <i className="fas fa-trash-alt fa-sm fa-fw mr-2 text-black-400"></i>Delete
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentCard(card);
                                    setPropertiesModalOpen(true);
                                  }}
                                >
                                  <i className="fas fa-info-circle fa-sm fa-fw mr-2 text-black-400"></i>Properties
                                </a>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Floating Button */}
                  <div id="floatingButtonContainer">
                    {selectedCards.length > 0 ? (
                      <button
                        id="deletefloatingButton"
                        className="btn btn-icon"
                        title="Delete"
                        onClick={handleDeleteSelected}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    ) : (
                      <button
                        id="addCardButton"
                        className="btn btn-icon-add"
                        title="Add Bank Card"
                        style={{ fontSize: '16px' }}
                        onClick={handleAddCard}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for dropdowns and tabs */}
      <style>
        {`
          /* Override the problematic CSS from forgot-password.css */
          .tab-content {
            display: block !important;
          }
          
          /* Custom styles for the dropdown behavior */
          .card-item-container {
            position: relative;
          }
          
          .dropdown-menu.show {
            display: block !important;
          }
          
          /* Add a small buffer area to prevent dropdown from disappearing */
          .dropdown-menu:before {
            content: '';
            position: absolute;
            top: -10px;
            left: 0;
            right: 0;
            height: 10px;
          }
        `}
      </style>

      {/* Modals */}
      <ViewCardModal
        show={viewModalOpen}
        onHide={() => setViewModalOpen(false)}
        cardData={currentCard}
      />

      <CardFormModal
        show={formModalOpen}
        onHide={() => setFormModalOpen(false)}
        initialData={isEditing ? currentCard : null}
        isEditing={isEditing}
        onSave={handleSaveCard}
        isLoading={isSaving}
      />

      <DeleteCardModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        cardName={currentCard?.originalName && removeTxtExtension(currentCard.originalName)}
        onConfirm={confirmDeleteCard}
        isSingle={true}
        isLoading={isDeleting}
      />

      <DeleteCardModal
        show={multipleDeleteModalOpen}
        onHide={() => setMultipleDeleteModalOpen(false)}
        onConfirm={confirmDeleteMultiple}
        isSingle={false}
        isLoading={isDeleting}
      />

      <AddToInheritanceModal
        show={inheritanceModalOpen}
        onHide={() => setInheritanceModalOpen(false)}
        onConfirm={confirmAddToInheritance}
        cardName={currentCard?.originalName}
        token={token}
        isLoading={isAddingToInheritance}
      />

      <PropertiesModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        itemData={currentCard}
        itemType="bankcard"
      />
    </div>
  );
};

export default Banking;